/**
 * 売上レポート（/money/:mode/sales）。サーバー: GET /v1/money/reports/sales（money/salesReport.ts・MCP の sales_report と同じ数字）。
 *
 * WHY 「売上」の画面（EarningsPage）に足さず別のタブにするか:
 *   - EarningsPage は「今いくらあって、いつ出金できるか」（残高の 4 段・出金の履歴）= 開いてすぐ見る画面。期間や切り口の選択を
 *     足すと、一番大事な残高のタイルが下に押しやられる
 *   - レポートは「期間 × 切り口（日 / 月 / ゲーム / 収入源）」を選んで眺める分析の画面で、操作が別物。MCP の sales_report と
 *     同じ API・同じ切り口にそろえると、AI に聞いた数字と画面の数字を突き合わせやすい
 *   - EarningsPage の収入源別・ゲーム別（/summary）は「全期間の累計」、こちらは「期間の発生額」。同じ表に混ぜると取り違える
 *
 * 数字の意味（サーバーの README「売上レポート」）: gross = 支払い総額、refunds = 返金・チャージバックで戻した額、
 * fees = gross − refunds − net（決済手数料など作者が受け取らなかった分すべて）、net = 作者の勘定に入った額（保留中を含む）。
 * 期間は仕訳の発生時刻（8 月の購入の 9 月の返金は 9 月の行）。ストア課金は数えない（お金が Kumo を通らない）。
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Empty, ErrorState, num, PageHeader, Spinner, StatTile } from '../../components/ui';
import { useResource } from '../../hooks';
import { formatDay, useT, type MessageKey } from '../../i18n';
import { money } from '../../money';
import type { SalesGroupBy, SalesReport } from '../../moneyApi';
import { groupSalesRows, recentRange, salesTotals, type SalesGroup } from '../../moneyLogic';
import { useMoney } from './MoneyShell';

/** 期間の選択肢（日数・今日を含む）。サーバーの上限は 3 年（1098 日）なので 3 年まで */
const RANGES = [30, 90, 365, 1095] as const;

const SOURCE_KEYS: Record<string, MessageKey> = {
  purchase: 'report.source.purchase',
  network_reward: 'report.source.network_reward',
  ad: 'report.source.ad',
  pass: 'report.source.pass'
};

export function SalesReportPage() {
  const { mode, livemode } = useMoney();
  const { t } = useT();
  const [days, setDays] = useState<(typeof RANGES)[number]>(30);
  const [groupBy, setGroupBy] = useState<SalesGroupBy>('day');
  const range = recentRange(new Date(), days);
  const r = useResource<SalesReport>(`/v1/money/reports/sales?livemode=${livemode}&from=${range.from}&to=${range.toInclusive}&groupBy=${groupBy}`);
  const rows = r.data?.rows ?? [];
  const groups = groupSalesRows(rows, groupBy);
  const totals = salesTotals(rows);
  const sourceLabel = (s: string) => (SOURCE_KEYS[s] ? t(SOURCE_KEYS[s]!) : s);

  const label = (g: SalesGroup) => {
    if (g.label.kind === 'source') return sourceLabel(g.label.value);
    if (g.label.kind === 'project')
      return g.label.value ? (
        <Link to={`/p/${g.label.value}/${mode === 'live' ? 'production' : 'development'}/payments`}>{g.label.name ?? g.label.value.slice(0, 8)}</Link>
      ) : (
        <span className="muted">{t('earn.unknownGame')}</span>
      );
    // 日は locale の日付、月は YYYY-MM のまま（formatDay は日付用）
    return groupBy === 'day' ? formatDay(g.label.value) : g.label.value;
  };

  return (
    <>
      <PageHeader eyebrow={t(`money.mode.${mode}`)} title={t('report.title')} />
      <p className="page-lead muted">{t('report.lead')}</p>

      <div className="form-inline">
        <label className="field">
          <span>{t('report.range')}</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value) as (typeof RANGES)[number])}>
            {RANGES.map((n) => (
              <option key={n} value={n}>
                {t('report.lastDays', { count: n })}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('report.groupBy')}</span>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as SalesGroupBy)}>
            <option value="day">{t('report.group.day')}</option>
            <option value="month">{t('report.group.month')}</option>
            <option value="project">{t('report.group.project')}</option>
            <option value="source">{t('report.group.source')}</option>
          </select>
        </label>
      </div>

      {r.loading && !r.data && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('report.title')} onRetry={r.reload} />}
      {r.data && rows.length === 0 && (
        <section className="card">
          <Empty title={t('report.emptyTitle')}>
            <p>{mode === 'test' ? t('earn.emptyBodyTest') : t('report.emptyBody')}</p>
          </Empty>
        </section>
      )}

      {r.data && rows.length > 0 && (
        <>
          {totals.map((tot) => (
            <section className="tiles" key={tot.currency} aria-label={`${t('report.total')} ${tot.currency.toUpperCase()}`}>
              <StatTile label={t('report.col.gross')} value={money(tot.gross, tot.currency)} hint={t('report.countHint', { count: tot.count })} />
              <StatTile label={t('report.col.refunds')} value={tot.refunds ? `−${money(tot.refunds, tot.currency)}` : money(0, tot.currency)} />
              <StatTile label={t('report.col.fees')} value={`−${money(tot.fees, tot.currency)}`} hint={t('report.feesHint')} />
              <StatTile label={t('report.col.net')} value={money(tot.net, tot.currency)} hint={t('report.netHint')} tone="accent" />
            </section>
          ))}

          <section className="card">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t(`report.col.${groupBy === 'project' ? 'project' : groupBy === 'source' ? 'source' : 'period'}`)}</th>
                    <th className="num">{t('report.col.count')}</th>
                    <th className="num">{t('report.col.gross')}</th>
                    <th className="num">{t('report.col.refunds')}</th>
                    <th className="num">{t('report.col.fees')}</th>
                    <th className="num">{t('report.col.net')}</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={g.key}>
                      <td>
                        {label(g)}
                        {/* 通貨が複数あるときだけ通貨を添える（USD だけの人に毎行 "USD" を見せない） */}
                        {totals.length > 1 && <span className="muted small"> {g.currency.toUpperCase()}</span>}
                        {groupBy !== 'source' && g.sources.length > 0 && (
                          <div className="muted small">{g.sources.map(sourceLabel).join(' · ')}</div>
                        )}
                      </td>
                      <td className="num">{num(g.count)}</td>
                      <td className="num">{money(g.gross, g.currency)}</td>
                      <td className="num muted">{g.refunds ? `−${money(g.refunds, g.currency)}` : '—'}</td>
                      <td className="num muted">{g.fees ? `−${money(g.fees, g.currency)}` : '—'}</td>
                      <td className="num">
                        <strong>{money(g.net, g.currency)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted small">{t('report.note')}</p>
          </section>
        </>
      )}
    </>
  );
}
